import { useCallback, useEffect, useState } from 'react';
import { DocType, LeadFilesRecord } from '../../../entities/lead/model/types';
import { pb } from '../../../shared/api/pocketbase';
import { pluralizeRu } from '../../../shared/lib/utils';
import { useLeads } from '../../../entities/lead';
import { CardModalFilesController } from './cardModalTypes';

interface Params {
  isOpen: boolean;
  leadId: string | null;
  onActivityChanged: () => Promise<void>;
  onNotify: (message: string) => void;
}

export const useCardModalFiles = ({
  isOpen,
  leadId,
  onActivityChanged,
  onNotify,
}: Params): CardModalFilesController => {
  const { getLeadFiles, uploadLeadFiles, deleteLeadFile, setLeadFileTypes } = useLeads();
  const [filesRecord, setFilesRecord] = useState<LeadFilesRecord | null>(null);
  const [fileToken, setFileToken] = useState<string>('');
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  const [isFilesUploading, setIsFilesUploading] = useState(false);
  const [deletingFileName, setDeletingFileName] = useState<string | null>(null);
  const [savingFile, setSavingFile] = useState<string | null>(null);

  const docTypes = filesRecord?.types ?? {};

  useEffect(() => {
    if (!isOpen || !leadId) {
      setFilesRecord(null);
      setFileToken('');
      setIsFilesLoading(false);
      setIsFilesUploading(false);
      setDeletingFileName(null);
      return;
    }

    setIsFilesLoading(true);
    Promise.all([getLeadFiles(leadId), pb.files.getToken()])
      .then(([record, token]) => {
        setFilesRecord(record);
        setFileToken(token);
      })
      .catch(error => {
        console.error('Failed to load lead files:', error);
        onNotify('Ошибка при загрузке вложений');
      })
      .finally(() => setIsFilesLoading(false));
  }, [getLeadFiles, isOpen, leadId, onNotify]);

  const handleFilesUpload = useCallback(async (files: File[]) => {
    if (!leadId || !files.length) {
      return;
    }

    setIsFilesUploading(true);
    try {
      const updated = await uploadLeadFiles(leadId, files);
      setFilesRecord(updated);
      await onActivityChanged();
      onNotify(
        files.length === 1
          ? 'Файл загружен'
          : `Загружено ${files.length} ${pluralizeRu(files.length, 'файл', 'файла', 'файлов')}`,
      );
    } catch (error) {
      console.error('Failed to upload files:', error);
      onNotify('Ошибка при загрузке файлов');
    } finally {
      setIsFilesUploading(false);
    }
  }, [leadId, onActivityChanged, onNotify, uploadLeadFiles]);

  const handleDeleteFile = useCallback(async (filename: string) => {
    if (!leadId) {
      return;
    }

    setDeletingFileName(filename);
    try {
      const updated = await deleteLeadFile(leadId, filename);
      setFilesRecord(updated);
      await onActivityChanged();
      onNotify('Файл удалён');
    } catch (error) {
      console.error('Failed to delete file:', error);
      onNotify('Ошибка при удалении файла');
    } finally {
      setDeletingFileName(null);
    }
  }, [deleteLeadFile, leadId, onActivityChanged, onNotify]);

  const handleSetFileType = useCallback(async (filename: string, type: DocType | null) => {
    if (!leadId) return;

    const current = filesRecord?.types ?? {};
    const next = { ...current };
    if (type) {
      next[filename] = type;
    } else {
      delete next[filename];
    }

    setSavingFile(filename);
    try {
      const updated = await setLeadFileTypes(leadId, next);
      setFilesRecord(prev => (prev ? { ...prev, types: updated.types ?? {} } : updated));
    } catch (error) {
      console.error('Failed to update doc type:', error);
      onNotify('Ошибка при обновлении готовности документов');
    } finally {
      setSavingFile(null);
    }
  }, [filesRecord, leadId, onNotify, setLeadFileTypes]);

  const getFileUrl = useCallback((filename: string, download = false) => {
    if (!filesRecord) return '';
    const options: Record<string, unknown> = { token: fileToken };
    if (download) options.download = true;
    return pb.files.getURL(filesRecord, filename, options);
  }, [filesRecord, fileToken]);

  const getFileUrlFresh = useCallback(async (filename: string, download = false) => {
    if (!filesRecord) return '';
    const token = await pb.files.getToken();
    setFileToken(token);
    const options: Record<string, unknown> = { token };
    if (download) options.download = true;
    return pb.files.getURL(filesRecord, filename, options);
  }, [filesRecord]);

  return {
    filesRecord,
    attachments: filesRecord?.images ?? [],
    docTypes,
    savingFile,
    isFilesLoading,
    isFilesUploading,
    deletingFileName,
    getFileUrl,
    getFileUrlFresh,
    handleFilesUpload,
    handleDeleteFile,
    handleSetFileType,
  };
};
